import { useEffect, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";
import { CHATWOOT_TOKEN, ensureChatwootLoaded } from "./chatwoot.web";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
interface ChatwootSheetProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Stable, app-side text mirroring the support sheet's open state.
 *
 * When Chatwoot is configured the sheet's visible UI lives entirely inside the
 * external `window.$chatwoot` widget (a third-party iframe with no app-owned
 * DOM), so "the support sheet is open" is otherwise unobservable from the app.
 * This marker is visually hidden (it never alters what real users see or touch)
 * but present in the accessibility tree, giving assistive tech — and E2E — a
 * deterministic signal that mounts on open and unmounts on close.
 */
export const CHATWOOT_SUPPORT_OPEN_MARKER = "Chat support opened";

function MissingConfigModal({
  visible,
  onClose,
}: Pick<ChatwootSheetProps, "visible" | "onClose">) {
  const capturePress = usePressCapture("MissingConfigModal");
  if (!visible) {
    return null;
  }

  return (
    <Modal visible animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>Chat support is not configured</Text>
          <Text style={styles.body}>
            Add `EXPO_PUBLIC_CHATWOOT_WEBSITE_TOKEN` to `src/apps/moon/.env` to
            enable Chatwoot on web.
          </Text>
          <Pressable
            onPress={() => {
              capturePress(pressLabel("Close"), "close_support_sheet");
              onClose();
            }}
            style={styles.button}
          >
            <Text style={styles.buttonText}>Close</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function ErrorModal({
  visible,
  onClose,
  message,
}: Pick<ChatwootSheetProps, "visible" | "onClose"> & { message: string }) {
  const capturePress = usePressCapture("SupportErrorModal");
  if (!visible) {
    return null;
  }

  return (
    <Modal visible animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>Unable to open chat</Text>
          <Text style={styles.body}>{message}</Text>
          <Pressable
            onPress={() => {
              capturePress(pressLabel("Close"), "close_support_sheet");
              onClose();
            }}
            style={styles.button}
          >
            <Text style={styles.buttonText}>Close</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

export function ChatwootSheet({ visible, onClose }: ChatwootSheetProps) {
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !CHATWOOT_TOKEN) {
      return;
    }

    setLoadError(null);

    ensureChatwootLoaded(() => {
      window.$chatwoot?.toggle?.("open");
    }, setLoadError);
  }, [visible]);

  useEffect(() => {
    if (visible || !window._chatwootReady) {
      return;
    }

    window.$chatwoot?.toggle?.("close");
  }, [visible]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (
        typeof e.data === "string" &&
        e.data.includes("chatwoot-widget:") &&
        e.data.includes('"closeWindow"')
      ) {
        onClose();
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onClose]);

  if (!CHATWOOT_TOKEN) {
    return <MissingConfigModal visible={visible} onClose={onClose} />;
  }

  // Chatwoot is configured: the external widget owns the visible UI. Render the
  // hidden open-state marker (deterministic, regardless of whether the remote
  // SDK finishes loading); surface the error modal only if the SDK fails.
  return (
    <>
      <SupportSheetMarker visible={visible} />
      {loadError ? (
        <ErrorModal visible={visible} onClose={onClose} message={loadError} />
      ) : null}
    </>
  );
}

function SupportSheetMarker({ visible }: Pick<ChatwootSheetProps, "visible">) {
  if (!visible) {
    return null;
  }

  return (
    <View pointerEvents="none" style={styles.marker}>
      <Text>{CHATWOOT_SUPPORT_OPEN_MARKER}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  marker: {
    position: "absolute",
    opacity: 0,
  },
  overlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "rgba(17, 17, 17, 0.45)",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    padding: 24,
    borderRadius: 24,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E5E5",
    gap: 12,
  },
  title: {
    color: "#1F1F1F",
    fontSize: 20,
    fontWeight: "700",
  },
  body: {
    color: "#555555",
    fontSize: 15,
    lineHeight: 22,
  },
  button: {
    alignSelf: "flex-start",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: "#1F1F1F",
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
});
